import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Button as MUIButton } from '@mui/material';
import useAppLanguage from '@/hooks/useAppLanguage';
import { UserInfo, Variables } from '@/types';
import Button from '../Button/Button';
import { IcoButtonSimple } from '../IcoButton/IcoButton';
import InputText from '../InputText/InputText';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import SimpleButton from '../SimpleButton/SimpleButton';
import TableListEditableNew, { TABLE_ROW_SELECTION } from '../TableListEditable/TableListEditableNew';
import styles from './OptionsCustom.module.scss';

const COLUMNS_NAMES = {
  CODE_VALUE: 'codeValue',
  DESCRIPTION: 'description',
};

interface OptionsCustomProps {
  id: string;
  value: string[];
  displayInTable?: boolean;
  popupTitle?: string;
  onChange: (users: string[] | string | null | UserInfo) => void;
  isMandatory?: boolean;
  onlyOneUser?: boolean;
  tableTitle?: string;
  buttonLabelAddCustom?: string;
  buttonLabelAddSelectableOption?: string;
  inputLabelAddCustom?: string;
  descriptionOfCustomOption?: string;
  validateNewOption?: (value: string, currentValues: string[]) => string[]; // Function to validate new option
  defaultOptions?: Variables[];
  fieldName?: string;
  disabled?: boolean;
}

const formatValue = (value: string) => value.replaceAll('{', '').replaceAll('}', '');

const OptionsCustom: React.FC<OptionsCustomProps> = (props) => {
  const { id, value = [], displayInTable = false, popupTitle = '', onChange, buttonLabelAddCustom, buttonLabelAddSelectableOption,
    inputLabelAddCustom, descriptionOfCustomOption, tableTitle, isMandatory = false, validateNewOption = () => [],
    defaultOptions = [], fieldName, disabled = false } = props;
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupAddOption, setPopupAddOption] = useState(false);
  const [valuesSelected, setValuesSelected] = useState<string[]>(value);
  const [valuesToAddSelected, setValuesToAddSelected] = useState<string[]>(value);
  const [selectionValues, setSelectionValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();
  const lang = useAppLanguage();

  const handleOnSave = () => {
    onChange(valuesSelected);
    setPopupOpen(false);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setValuesSelected(value);
  };

  const columns = useMemo(() => [
    { name: COLUMNS_NAMES.CODE_VALUE, title: t('value'), flex: 2 },
    { name: COLUMNS_NAMES.DESCRIPTION, title: t('descriptionLabel'), flex: 2 },
  ], [t]);

  const valueCompleteSelected = useMemo(() => valuesSelected.map((valueS) => {
    const valueFormatted = formatValue(valueS);
    const completeOption = defaultOptions.find((option) => option.code === valueFormatted);

    return {
      codeValue: valueFormatted,
      description: completeOption?.name?.[lang] ?? descriptionOfCustomOption,
    };
  }), [valuesSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnAddCustomOption = () => {
    const newOption = inputValue.trim();
    setValuesSelected([...valuesSelected, newOption]);
    setInputValue('');
  };

  const handleDeleteSelectedValues = () => {
    const opSelected = selectionValues.map((op) => {
      const isVariable = defaultOptions.some((option) => option.code === op);

      return isVariable ? `{{${op}}}` : op;
    });
    const newValuesSelected = valuesSelected.filter((op) => !opSelected.includes(op));
    setValuesSelected(newValuesSelected);
    setSelectionValues([]);
  };

  const validNewValue = validateNewOption(inputValue, valuesSelected);

  const optionWithSelectedFiltered = useMemo(() => defaultOptions
    .filter((option) => !valuesSelected.includes(`{{${option.code}}}`))
    .map((option) => ({ codeValue: option.code, description: option.name?.[lang] })),
  [defaultOptions, valuesSelected, lang]);

  const onClosePopupAddOption = () => {
    setPopupAddOption(false);
    setValuesToAddSelected([]);
  };
  const onAddNewOptionSelected = () => {
    setPopupAddOption(false);
    const newValues = [...valuesSelected, `{{${valuesToAddSelected[0]}}}`];
    setValuesSelected(newValues);
  };

  return (
    <>
      <div className={`${styles.OptionsSummary} ${!displayInTable ? styles.NoTable : ''}`} onClick={(!displayInTable && !disabled) ? () => setPopupOpen(true) : () => null}>
        <div className={`${styles.OptionsSummaryOp} ${valuesSelected.length === 0 ? styles.NoOptions : ''}`}>
          { valuesSelected.length !== 0
            ? (valuesSelected.map((optionS) => (<div key={optionS} className={styles.OptionV}>{formatValue(optionS)}</div>)))
            : t('noOptions') }
        </div>
        <div>
          <IconButton disabled={disabled} id={id} data-testid={id} onClick={() => setPopupOpen(true)} size="small">
            <i className={`di icon-stift-bearbeiten ${styles.Icon}`} />
            {isMandatory && <MandatoryLabel className={`${styles.MandatoryIcon} error`} />}
          </IconButton>
        </div>
        {!displayInTable && fieldName && <div className={styles.LabelField}>{fieldName}</div>}
      </div>
      {(popupOpen) && (
        <Dialog id={id} data-testid={id} open={popupOpen} maxWidth={false}>
          <DialogTitle>{popupTitle}</DialogTitle>
          <DialogContent className={styles.DialogContent}>
            <>
              <div className={styles.AddOptionsSection}>
                <div className={styles.AddCustomOptionSection}>
                  <div className={styles.InputNewCustomOption}>
                    <InputText
                      id="input-custom-email-option"
                      label={inputLabelAddCustom}
                      value={inputValue}
                      handleChange={(e) => setInputValue(e.target.value)}
                      invalid={validNewValue.length > 0}
                      helperText={validNewValue || undefined}
                      className="w-100"
                    />
                  </div>
                  <SimpleButton
                    handleClick={handleOnAddCustomOption}
                    id="btn-add-custom-option"
                    color="primary"
                    textStyle={styles.GiveAuthButton}
                    buttonText={buttonLabelAddCustom || ''}
                    disabled={inputValue.trim() === '' || validNewValue.length > 0}
                  />
                </div>
                <div className={styles.AddSelectableOptionButton}>
                  <Button
                    handleClick={() => setPopupAddOption(true)}
                    id="btn-open-add-option-popup"
                    btnText={buttonLabelAddSelectableOption || ''}
                    buttonStyle="PrimaryRound"
                  />
                </div>
              </div>
              <Divider />
            </>
            <div>
              <div className={styles.TableUsersHeader}>
                <DialogTitle className={styles.TableUsersTitle}>{tableTitle}</DialogTitle>
                <div className={styles.ButtonsContentTableRight}>
                  <IcoButtonSimple
                    disabled={!(selectionValues.length > 0)}
                    id="btn-delete-option"
                    icon="icon-muelleimer-loeschen"
                    tooltipText={t('software.delete')}
                    handleClick={handleDeleteSelectedValues}
                  />
                </div>
              </div>
              <TableListEditableNew
                list={valueCompleteSelected}
                columns={columns}
                hasCheckbox
                columnId="codeValue"
                selection={selectionValues}
                onSelectionChange={(selectedRows) => setSelectionValues(selectedRows as string[])}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <MUIButton variant="contained" color="primary" onClick={handleOnSave} disabled={value === valuesSelected} data-testid="save-optionscustom-popup">
              {t('dialog.save')}
            </MUIButton>
            <MUIButton color="primary" onClick={handleClosePopup} data-testid="cancel-optionscustom-popup">{t('dialog.cancel')}</MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {popupAddOption && (
        <Dialog open maxWidth="md" fullWidth onClose={onClosePopupAddOption}>
          <DialogTitle>{t('dialog.addVariables')}</DialogTitle>
          <DialogContent>
            <TableListEditableNew
              list={optionWithSelectedFiltered}
              columns={columns}
              hasCheckbox
              rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
              isFiltering
              columnId="codeValue"
              selection={valuesToAddSelected}
              onSelectionChange={(selectedRows) => setValuesToAddSelected(selectedRows as string[])}
            />
          </DialogContent>
          <div className={`${styles.ButtonsTable} ${styles.BottomButtons}`}>
            <MUIButton id="email-vars-back" onClick={onClosePopupAddOption}>{t('dialog.back')}</MUIButton>
            <MUIButton id="email-vars-apply" variant="contained" color="primary" onClick={onAddNewOptionSelected} disabled={valuesToAddSelected.length === 0}>
              {t('dialog.setVariable')}
            </MUIButton>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default OptionsCustom;
